/**
 * Contact Form Component for WoodAlps Customer Inquiries
 * 
 * This is a comprehensive contact form with advanced features:
 * - Multi-step form validation with real-time feedback
 * - Spam protection using Cloudflare Turnstile
 * - Honeypot field for additional bot protection
 * - Country selection with custom "Other" option
 * - Character count for message field
 * - Comprehensive error handling and user feedback
 * - Form submission to Next.js API endpoint
 * 
 * Security Features:
 * - Turnstile CAPTCHA integration
 * - Honeypot field (hidden from users)
 * - Form validation on both client and server
 * - Rate limiting protection via API
 * 
 * @description Advanced contact form with spam protection and validation
 */

import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import Turnstile from 'react-turnstile';
import { useTranslation } from '../../src/hooks/useTranslation';

const   ContactForm = ({ onFormSuccess }) => {
  const { t } = useTranslation();
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    otherCountry: '',
    city: '',
    message: '',
    referralSource: '',
    otherReferralSource: '',
    ambassadorDistributor: '',
    // Add honeypot field
    website: '',
    // Turnstile token field
    turnstileToken: ''
  });

  // Form status
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);

  // Call onFormSuccess when submitStatus changes to 'success'
  useEffect(() => {
    if (submitStatus === 'success' && onFormSuccess) {
      onFormSuccess();
    }
  }, [submitStatus, onFormSuccess]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle character count for message field
    if (name === 'message') {
      setCharCount(value.length);
    }
    
    // Handle phone number validation (only numbers and optional + at beginning)
    if (name === 'phone' && value !== '') {
      const isValidPhone = /^(\+?\d*)$/.test(value);
      if (!isValidPhone) return;
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Handle Turnstile verification
  const handleTurnstileVerify = (token) => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: token
    }));
    
    // Clear error if it exists
    if (errors.turnstile) {
      setErrors(prev => ({
        ...prev,
        turnstile: null
      }));
    }
  };

  // Handle Turnstile error
  const handleTurnstileError = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }));
    
    setErrors(prev => ({
      ...prev,
      turnstile: 'Security verification failed. Please try again.'
    }));
  };

  // Handle Turnstile expiry
  const handleTurnstileExpire = () => {
    setFormData(prev => ({
      ...prev,
      turnstileToken: ''
    }));
    
    setErrors(prev => ({
      ...prev,
      turnstile: 'Security verification expired. Please complete it again.'
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.title) newErrors.title = "Please select a title";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    if (!formData.country) newErrors.country = "Please select a country";
    if (formData.country === 'other' && !formData.otherCountry.trim()) {
      newErrors.otherCountry = "Please specify your country";
    }
    if (!formData.city) newErrors.city = "Please enter a city";
    if (!formData.message) newErrors.message = "Message is required";
    if (!formData.referralSource) newErrors.referralSource = "Please let us know how you heard about us";
    
    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    
    // Message length validation
    if (formData.message && formData.message.length > 500) {
      newErrors.message = "Message cannot exceed 500 characters";
    }
    
    // Validate referral source options
    if (formData.referralSource === 'other' && !formData.otherReferralSource.trim()) {
      newErrors.otherReferralSource = "Please specify how you heard about us";
    } else if (formData.referralSource === 'ambassador' && !formData.ambassadorDistributor) {
      newErrors.ambassadorDistributor = "Please select an ambassador or distributor";
    }
    
    // Turnstile validation
    if (!formData.turnstileToken) {
      newErrors.turnstile = "Please complete the security check";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const isValid = validateForm();
    if (!isValid) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Submit form data to API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        
        // Scroll to top to show thank you message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Reset form after successful submission
        setFormData({
          title: '',
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          country: '',
          otherCountry: '',
          city: '',
          message: '',
          referralSource: '',
          otherReferralSource: '',
          ambassadorDistributor: '',
          website: '',
          turnstileToken: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If form was successfully submitted, only show success message
  if (submitStatus === 'success') {
    return (
      <div className="bg-green-100 border border-green-200 text-green-800 p-8 rounded-md flex flex-col items-center text-center">
        <CheckCircle className="w-16 h-16 mb-4 text-black" />
        <h3 className="text-xl font-bold mb-2 font-montserrat">{t('contact.thank_you_title')}</h3>
        <p className="text-lg mb-4">{t('contact.thank_you_message')}</p>
        <p>{t('contact.thank_you_message_2')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Form Status Messages - Only show error message now */}
      {submitStatus === 'error' && (
        <div className="bg-red-100 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-start mb-4">
          <AlertCircle className="w-5 h-5 mr-2 mt-0.5" />
          <span>{t('contact.form_error')}</span>
        </div>
      )}

      {/* Honeypot field - hidden from users, but bots may fill it */}
      <div className="opacity-0 absolute top-0 left-0 h-0 w-0 -z-10 overflow-hidden">
        <label htmlFor="website">Website</label>
        <input 
          type="text" 
          id="website" 
          name="website" 
          onChange={handleChange}
          value={formData.website}
          tabIndex="-1" 
          autoComplete="off" 
        />
      </div>

      {/* Title, First Name, Last Name Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.title_label')} <span className="text-red-500">*</span>
          </label>
          <select
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat bg-wood-brown/5 text-black`}
            required
          >
            <option value="">{t('contact.select_label')}</option>
            <option value="M.">{t('contact.title_option_1')}</option>
            <option value="Mrs.">{t('contact.title_option_2')}</option>
            <option value="Other">{t('contact.title_option_3')}</option>
          </select>
          {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
        </div>
        
        {/* First Name */}
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.first_name_label')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.firstName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat`}
            required
          />
          {errors.firstName && <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>}
        </div>
        
        {/* Last Name */}
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.last_name_label')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.lastName ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat`}
            required
          />
          {errors.lastName && <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>}
        </div>
      </div>

      {/* Email and Phone Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.email_label')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat`}
            required
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>
        
        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.phone_label')} <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+33612345678"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat"
          />
        </div>
      </div>

      {/* Country and City Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Country */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.country_label')} <span className="text-red-500">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat bg-wood-brown/5 text-black`}
            required
          >
            <option value="">{t('contact.select_label_country')}</option>
            <option value="FR">{t('contact.country_option_1')}</option>
            <option value="CH">{t('contact.country_option_2')}</option>
            <option value="DE">{t('contact.country_option_3')}</option>
            <option value="IT">{t('contact.country_option_4')}</option>
            <option value="UK">{t('contact.country_option_5')}</option>
            <option value="US">{t('contact.country_option_6')}</option>
            <option value="other">{t('contact.country_option_7')}</option>
          </select>
          {errors.country && <p className="mt-1 text-sm text-red-500">{errors.country}</p>}
        </div>
        
        {/* Other Country field that appears when "other" is selected */}
        {formData.country === 'other' && (
          <div>
            <label htmlFor="otherCountry" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
              {t('contact.please_specify_country')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                id="otherCountry"
                name="otherCountry"
                value={formData.otherCountry}
                onChange={handleChange}
                maxLength={15}
                className={`w-full px-3 py-2 border ${errors.otherCountry ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat pr-16`}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-xs text-gray-500">
                  {formData.otherCountry.length}/15
                </span>
              </div>
            </div>
            {errors.otherCountry && <p className="mt-1 text-sm text-red-500">{errors.otherCountry}</p>}
          </div>
        )}
        
        {/* City */}
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
            {t('contact.city_label')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.city ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat`}
            required
          />
          {errors.city && <p className="mt-1 text-sm text-red-500">{errors.city}</p>}
        </div>
      </div>

      {/* Message */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
          {t('contact.message_label')} <span className="text-red-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="5"
          maxLength="500"
          className={`w-full px-3 py-2 border ${errors.message ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat`}
          required
        ></textarea>
        <div className="flex justify-between mt-1">
          <span className={`text-sm ${charCount > 450 ? (charCount > 500 ? 'text-red-500' : 'text-amber-600') : 'text-gray-500'}`}>
            {charCount}/{t('contact.message_max_length')} 
          </span>
          {errors.message && <p className="text-sm text-red-500">{errors.message}</p>}
        </div>
      </div>

      {/* How did you hear about us? */}
      <div>
        <label htmlFor="referralSource" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
          {t('contact.referral_source_label')} <span className="text-red-500">*</span>
        </label>
        <select
          id="referralSource"
          name="referralSource"
          value={formData.referralSource}
          onChange={handleChange}
          className={`w-full px-3 py-2 border ${errors.referralSource ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat bg-wood-brown/5 text-black`}
          required
        >
          <option value="">{t('contact.select_label')}</option>
          <option value="search">{t('contact.referral_source_option_1')}</option>
          <option value="facebook">{t('contact.referral_source_option_2')}</option>
          <option value="instagram">{t('contact.referral_source_option_3')}</option>
          <option value="friend">{t('contact.referral_source_option_4')}</option>
          <option value="magazine">{t('contact.referral_source_option_5')}</option>
          <option value="exhibition">{t('contact.referral_source_option_6')}</option>
          <option value="ambassador">{t('contact.referral_source_option_7')}</option>
          <option value="other">{t('contact.referral_source_option_8')}</option>
        </select>
        {errors.referralSource && <p className="mt-1 text-sm text-red-500">{errors.referralSource}</p>}
        
        {formData.referralSource === 'ambassador' && (
          <div className="mt-3">
            <label htmlFor="ambassadorDistributor" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
              {t('contact.select_ambassador_distributor_label')} <span className="text-red-500">*</span>
            </label>
            <select
              id="ambassadorDistributor"
              name="ambassadorDistributor"
              value={formData.ambassadorDistributor}
              onChange={handleChange}
              className={`w-full px-3 py-2 border ${errors.ambassadorDistributor ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat bg-wood-brown/5 text-black`}
            >
              <option value="">{t('contact.select_label')}</option>
              <option value="clément">{t('contact.ambassador_distributor_option_1')}</option>
              <option value="fabrice">{t('contact.ambassador_distributor_option_2')}</option>
              <option value="fabio">{t('contact.ambassador_distributor_option_3')}</option>
              <option value="yann">{t('contact.ambassador_distributor_option_4')}</option>
            </select>
            {errors.ambassadorDistributor && (
              <p className="mt-1 text-sm text-red-500">{errors.ambassadorDistributor}</p>
            )}
          </div>
        )}
        
        {formData.referralSource === 'other' && (
          <div className="mt-3">
            <label htmlFor="otherReferralSource" className="block text-sm font-medium text-gray-700 mb-1 font-montserrat">
              {t('contact.please_specify')} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                id="otherReferralSource"
                name="otherReferralSource"
                value={formData.otherReferralSource}
                onChange={handleChange}
                maxLength={100}
                placeholder="Please tell us how you heard about us..."
                className={`w-full px-3 py-2 border ${errors.otherReferralSource ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-forest-green focus:border-transparent font-montserrat pr-16`}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span className="text-xs text-gray-500">
                  {formData.otherReferralSource.length}/100
                </span>
              </div>
            </div>
            {errors.otherReferralSource && (
              <p className="mt-1 text-sm text-red-500">{errors.otherReferralSource}</p>
            )}
          </div>
        )}
      </div>

      {/* Cloudflare Turnstile (Anti-Spam) - Mobile responsive */}
      <div>
        <div className="mb-2 flex justify-center">
          <div className="w-full max-w-[320px] sm:max-w-[300px] overflow-hidden">
            <div className="transform scale-90 sm:scale-100 origin-center"></div>
              <Turnstile
                sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="auto"
                refreshExpired="auto"
                retry="auto"
                size="normal"
              />
            </div>
          </div>
        </div>
        {errors.turnstile && <p className="mt-1 text-sm text-red-500 text-center font-montserrat">{errors.turnstile}</p>}
        



      {/* Submit Button */}
      <div className="flex justify-center">
        <button
          type="submit"
          disabled={isSubmitting || !formData.turnstileToken}
          className="w-48 flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-white bg-accent-color hover:bg-forest-green/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-green transition-all duration-200 font-montserrat font-medium disabled:opacity-50"

        >
          {isSubmitting ? (
            <>
              <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
              {t('contact.sending')}
            </>
          ) : (
            t('contact.submit')
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm; 